import { Link } from "wouter";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Check, Info, Home, Building2, HardHat } from "lucide-react";
import { saunaServices, type SaunaService, type SaunaSlug } from "@/data/sauna-services";

const ember = "hsl(18 82% 52%)";
const emberSoft = "hsl(22 90% 66%)";
const img = (f: string) => `${import.meta.env.BASE_URL}images/${f}`;

const serviceImages: Record<SaunaSlug, { src: string; alt: string }> = {
  "advise-design": { src: img("sauna-engineer-cover.webp"), alt: "VertexAir engineer assessing a sauna interior" },
  supply: { src: img("sauna-supply-delivery.webp"), alt: "Wrapped sauna equipment secured on a delivery vehicle" },
  install: { src: img("sauna-assembly.webp"), alt: "Engineers assembling a timber sauna cabin" },
  "maintenance-repair": { src: img("sauna-detail-3.webp"), alt: "Sauna control panel" },
};

const audienceIcons = [Home, Building2, HardHat];

export function SaunaServiceLower({ s }: { s: SaunaService }) {
  const reduce = useReducedMotion();
  const rise = (d = 0) =>
    reduce
      ? {}
      : { initial: { opacity: 0, y: 12 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.4, delay: d } };
  const others = saunaServices.filter((o) => o.slug !== s.slug);

  return (
    <>
      {/* Prepare + note */}
      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 max-w-7xl grid lg:grid-cols-12 gap-6">
          <motion.div {...rise()} className="lg:col-span-5 rounded-2xl border border-border bg-card p-7 lg:p-9">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-xs uppercase tracking-[0.18em]" style={{ color: ember }}>Checklist</span>
              <span className="h-px flex-1 bg-border" />
              <span className="font-mono text-xs text-muted-foreground">{String(s.prepare.length).padStart(2, "0")} items</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold font-display mb-6">Useful to have ready</h2>
            <ol className="divide-y divide-border">
              {s.prepare.map((p, i) => (
                <li key={p} className="flex gap-4 py-3.5 first:pt-0 last:pb-0">
                  <span className="font-mono text-xs text-muted-foreground pt-1 w-5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <span className="flex-1 text-foreground/85 leading-relaxed">{p}</span>
                  <Check className="w-4 h-4 mt-1 shrink-0 text-primary" aria-hidden />
                </li>
              ))}
            </ol>
          </motion.div>

          <motion.div {...rise(0.08)} className="lg:col-span-7 relative overflow-hidden rounded-2xl text-white p-7 lg:p-9 flex flex-col"
            style={{ background: "linear-gradient(150deg, hsl(222 47% 12%) 0%, hsl(218 50% 17%) 70%, hsl(14 45% 20%) 140%)" }}>
            <div aria-hidden className="absolute -right-24 -top-24 w-72 h-72 rounded-full opacity-30 blur-3xl" style={{ background: `radial-gradient(circle, ${ember}, transparent 65%)` }} />
            <div className="relative flex items-start gap-4 mb-8">
              <span className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "hsl(18 82% 52% / 0.18)" }}>
                <Info className="w-5 h-5" style={{ color: emberSoft }} aria-hidden />
              </span>
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-display mb-3">{s.note.title}</h2>
                <p className="text-white/70 leading-relaxed">{s.note.body}</p>
              </div>
            </div>
            <p className="relative text-xs uppercase tracking-[0.2em] text-white/50 mb-3">Who we work with</p>
            <ul className="relative mt-auto grid sm:grid-cols-3 gap-3">
              {s.audiences.map((a, i) => {
                const Icon = audienceIcons[i % audienceIcons.length];
                return (
                  <li key={a.who} className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
                    <Icon className="w-4 h-4 mb-3" style={{ color: emberSoft }} aria-hidden />
                    <p className="font-display font-bold mb-1">{a.who}</p>
                    <p className="text-sm text-white/65 leading-snug">{a.body}</p>
                  </li>
                );
              })}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Other services */}
      <section className="pb-16 lg:pb-24">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex items-end justify-between gap-4 mb-6">
            <h2 className="text-2xl md:text-3xl font-bold font-display">Other sauna services</h2>
            <span className="font-mono text-xs text-muted-foreground hidden sm:block">{s.index} / 04</span>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {others.map((o, i) => {
              const im = serviceImages[o.slug];
              return (
                <motion.div key={o.slug} {...rise(i * 0.06)}>
                  <Link href={o.href} data-testid={`link-sauna-related-${o.slug}`}
                    className="group flex flex-col h-full rounded-2xl border border-border bg-card overflow-hidden transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">
                    <span className="relative block aspect-[16/10] overflow-hidden bg-secondary">
                      <img src={im.src} alt={im.alt} loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 motion-safe:group-hover:scale-[1.04]" />
                      <span aria-hidden className="absolute inset-0" style={{ background: "linear-gradient(to top, hsl(222 47% 11% / 0.55), transparent 55%)" }} />
                      <span className="absolute left-4 bottom-3 font-mono text-xs text-white/90">{o.index}</span>
                      <span className="absolute right-3 top-3 w-9 h-9 rounded-full bg-white/90 text-foreground flex items-center justify-center transition-transform motion-safe:group-hover:-translate-y-0.5 motion-safe:group-hover:translate-x-0.5">
                        <ArrowUpRight className="w-4 h-4" aria-hidden />
                      </span>
                    </span>
                    <span className="flex flex-col p-5">
                      <span className="font-display font-bold text-lg group-hover:text-primary transition-colors">{o.navTitle}</span>
                      <span className="block text-sm text-muted-foreground mt-1.5 leading-relaxed">{o.navDesc}</span>
                    </span>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
